import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Clock, CheckCircle2, XCircle, ArrowRight, Play, Flame, Zap, Activity } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/sessions/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  const active = orders.filter((o) => o.payment_status === "paid");
  const focusSeries = stats?.focus_series || [];
  const daily = stats?.daily || [];

  const statCards = [
    { icon: Activity, label: "Sessions", value: stats?.total_sessions ?? 0 },
    { icon: Clock, label: "Minutes trained", value: stats?.total_minutes ?? 0 },
    { icon: Zap, label: "Avg focus", value: stats?.avg_focus ?? 0 },
    { icon: Flame, label: "Day streak", value: stats?.streak ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-soma-dashbg">
      <Nav />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-32 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Your dashboard</span>
            <h1 className="font-display font-semibold text-3xl lg:text-4xl mt-3">Hello, {user?.name?.split(" ")[0]}.</h1>
          </div>
          <Link to="/player" data-testid="start-session-cta"
            className="group flex items-center gap-3 px-7 py-3.5 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 cyan-glow">
            <Play className="w-4 h-4" fill="currentColor" /> Start a session
          </Link>
        </motion.div>

        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-soma-border border border-soma-border mt-10">
          {statCards.map((s, i) => (
            <div key={i} className="bg-soma-dashbg p-6" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <s.icon className="w-4 h-4 text-soma-primary mb-4" strokeWidth={1.5} />
              <div className="font-display font-semibold text-3xl text-white tabular-nums">{s.value}</div>
              <div className="text-xs text-soma-muted mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* charts */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          <div className="lg:col-span-3 border border-soma-border bg-soma-surface p-6" data-testid="focus-chart">
            <h3 className="text-xs uppercase tracking-[0.2em] text-soma-muted mb-6">Focus trend</h3>
            {focusSeries.length < 2 ? (
              <div className="h-56 flex items-center justify-center text-soma-muted text-sm text-center px-6">
                {focusSeries.length === 0 ? "Train a session to see your focus curve." : "One session logged — train once more to plot your trend."}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={224}>
                <AreaChart data={focusSeries} margin={{ left: -20, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#133538" vertical={false} />
                  <XAxis dataKey="t" stroke="#8EAAAB" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} stroke="#8EAAAB" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#081C1E", border: "1px solid #133538", borderRadius: 0, color: "#fff" }} />
                  <Area type="monotone" dataKey="focus" stroke="#00E5FF" strokeWidth={2} fill="url(#fg)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="lg:col-span-2 border border-soma-border bg-soma-surface p-6" data-testid="minutes-chart">
            <h3 className="text-xs uppercase tracking-[0.2em] text-soma-muted mb-6">This week (min)</h3>
            <ResponsiveContainer width="100%" height={224}>
              <BarChart data={daily} margin={{ left: -20, right: 8, top: 8 }}>
                <CartesianGrid stroke="#133538" vertical={false} />
                <XAxis dataKey="day" stroke="#8EAAAB" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#8EAAAB" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(0,229,255,0.06)" }} contentStyle={{ background: "#081C1E", border: "1px solid #133538", borderRadius: 0, color: "#fff" }} />
                <Bar dataKey="minutes" fill="#00E5FF" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* recent sessions */}
        <div className="mt-16">
          <h2 className="font-display text-xl lg:text-2xl font-bold mb-6">Recent sessions</h2>
          {sessions.length === 0 ? (
            <div className="border border-soma-border bg-soma-surface p-12 text-center" data-testid="no-sessions">
              <Brain className="w-9 h-9 text-soma-primary mx-auto mb-5" strokeWidth={1.5} />
              <p className="text-soma-muted mb-7">No training sessions yet. Your first one takes two minutes.</p>
              <Link to="/player" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors">
                <Play className="w-4 h-4" fill="currentColor" /> Start now
              </Link>
            </div>
          ) : (
            <div className="border border-soma-border divide-y divide-soma-border" data-testid="sessions-list">
              {sessions.slice(0, 8).map((s) => (
                <div key={s.id} className="bg-soma-surface p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display font-semibold text-white">{s.program_name}</div>
                    <div className="text-sm text-soma-muted">{Math.round(s.duration_sec / 60)} min · {new Date(s.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-soma-primary font-display font-semibold text-lg tabular-nums">{Math.round(s.avg_focus)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-soma-muted">avg</div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-display font-semibold text-lg tabular-nums">{Math.round(s.best_focus)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-soma-muted">peak</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* subscriptions / orders */}
        <div className="mt-16">
          <h2 className="font-display text-xl lg:text-2xl font-bold mb-6">Subscriptions & orders</h2>
          {orders.length === 0 ? (
            <div className="border border-soma-border bg-soma-surface p-12 text-center" data-testid="no-orders">
              <p className="text-soma-muted mb-7">You haven't subscribed to a program yet.</p>
              <Link to="/programs" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/15 hover:border-soma-primary transition-colors">
                Browse programs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="border border-soma-border divide-y divide-soma-border" data-testid="orders-list">
              {orders.map((o) => {
                const paid = o.payment_status === "paid";
                return (
                  <div key={o.id} className="bg-soma-surface p-5 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-display font-semibold text-white">{o.program_name}</div>
                      <div className="text-sm text-soma-muted">${o.amount} {o.currency?.toUpperCase()}</div>
                    </div>
                    <div className={`flex items-center gap-2 ${paid ? "text-soma-accent" : "text-soma-muted"}`}>
                      {paid ? <CheckCircle2 className="w-4 h-4" /> : o.payment_status === "pending" ? <Clock className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      <span className="text-sm capitalize">{paid ? "Active" : o.payment_status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
